import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useEventBus } from '@vueuse/core';

import { useFeedbackBar } from '@js/composables/useFeedbackBar';
import { useStudentStore } from '@js/stores/student/student';

export const propertyDefaultState = {
  id: null,
  shortlisted: false,
};

async function addToShortlist(propertyId) {
  if (!propertyId) return;
  return await axios.post(`/student/shortlist/add/${propertyId}`);
}

async function removeFromShortlist(propertyId) {
  if (!propertyId) return;
  return await axios.post(`/student/shortlist/remove/${propertyId}`);
}

export const PROPERTIES_EVENT_BUS_KEY = 'properties-event-bus';
const eventBus = useEventBus(PROPERTIES_EVENT_BUS_KEY);

export const usePropertiesStore = defineStore('properties', () => {
  const feedbackBar = ref(useFeedbackBar());

  const currentProperty = ref(propertyDefaultState);

  const showIncentives = ref(false);

  const propertiesList = ref([]);

  const shortlistToast = ref(resetShortlistToast());

  function resetShortlistToast() {
    return {
      open: false,
      address: null,
      timer: 0,
    };
  }

  function openShortlistToast(address = null, duration = 3000) {
    shortlistToast.value.open = true;

    eventBus.emit('open-toast', address);

    if (shortlistToast.value.timer !== 0) {
      clearInterval(shortlistToast.value.timer);
    }

    shortlistToast.value.timer = setTimeout(() => {
      eventBus.emit('close-toast');

      shortlistToast.value = resetShortlistToast();
    }, duration);
  }

  function setPropertiesList(properties) {
    propertiesList.value = properties;
  }

  function getPropertyIndex(propertyId) {
    return propertiesList.value.findIndex(
      (property) => property.id === propertyId
    );
  }

  async function toggleShortlisted(propertyId) {
    const index = getPropertyIndex(propertyId);
    if (index === -1) {
      // Can't find the property in the list
      throw new Error(
        `Cannot update shortlisted state of property #${propertyId}`
      );
    }

    const state = propertiesList.value[index].shortlisted;

    let response;
    if (state) {
      // Value is true
      response = await removeFromShortlist(propertyId);
    } else {
      // Value is false or null
      response = await addToShortlist(propertyId);
    }

    const { success, count } = response.data;
    if (!success) {
      feedbackBar.value.show(
        'An error occurred, please try again later.',
        'error'
      );
      return {
        state,
        count,
      };
    }

    const isShortlisted = !state;
    propertiesList.value[index].shortlisted = isShortlisted;

    const studentStore = useStudentStore();
    studentStore.setShortlistCount(count);

    return {
      state: isShortlisted,
      count,
    };
  }

  function setCurrentShortlisted(value) {
    if (!currentProperty.value) {
      throw new Error(
        'Cannot update shortlisted state of current property: null'
      );
    }
    currentProperty.value.shortlisted = value;
  }

  return {
    currentProperty,
    showIncentives,
    propertiesList,
    setCurrentShortlisted,
    setPropertiesList,
    toggleShortlisted,
    openShortlistToast,
  };
});
